import axios from "axios";
import { Messages } from "../constants/Messages";
import { normalizeString } from "../utils/AlertUtils";
import { validateArrayInput } from "../utils/ApiValidationUtils";
import { setupAPIRequestInterceptor, setupAPIResponseInterceptor } from "../utils/Authorization";
import { mapMaterialSize, mapMaterialTypes } from "../utils/MaterialsUtils";

// Base URL configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "https://api.materiallogs.barkbyborders.com";
const materialAPI = axios.create({
    baseURL: `${API_BASE_URL}/materials`,
});

setupAPIRequestInterceptor(materialAPI);
setupAPIResponseInterceptor(materialAPI);

type CacheEntry<T> = {
    data: T;
    expiresAt: number;
};

const MATERIAL_CACHE_TTL_MS = Number(process.env.REACT_APP_MATERIAL_CACHE_TTL_MS ?? 5 * 60 * 1000);

const isCacheValid = <T>(entry: CacheEntry<T> | null) =>
    Boolean(entry && entry.expiresAt > Date.now());

const cloneArray = <T extends Record<string, unknown>>(input: T[]) =>
    input.map((item) => ({ ...item }));

let materialTypesCache: CacheEntry<any[]> | null = null;
let materialSizesCache: CacheEntry<any[]> | null = null;

const primeMaterialTypesCache = (data: any[]) => {
    materialTypesCache = {
        data,
        expiresAt: Date.now() + MATERIAL_CACHE_TTL_MS,
    };
};

const primeMaterialSizesCache = (data: any[]) => {
    materialSizesCache = {
        data,
        expiresAt: Date.now() + MATERIAL_CACHE_TTL_MS,
    };
};

const invalidateMaterialTypesCache = () => {
    materialTypesCache = null;
};

const invalidateMaterialSizesCache = () => {
    materialSizesCache = null;
};

/* =======================
 Inventory API Calls
 ======================= */

/**
 * Create a new inventory record or multiple records
 */
export const createInitialInventory = async (data: any[]): Promise<any> => {
    try {
        validateArrayInput(data); // Ensure input is valid
        const processedData = data.map((item) => ({
            ...item,
            color_code: item.color_code?.trim() || normalizeString(`${item.material_type_id}-${item.color_name?.trim()}`),
        }));
        const response = await materialAPI.post("/records", processedData); // Send the API request
        return response.data; // Return response data on success
    } catch (error: any) {

        // Extract detailed error information if available
        const errorResponse = error.response?.data;
        const errorMessage = errorResponse?.message || Messages.ERROR_CREATE;
        const errors = errorResponse?.errors || []; // Include specific errors if present

        // Throw a structured error object
        throw {
            message: errorMessage,
            errors, // Pass specific error details
        };
    }
};

/**
 * Update existing inventory records
 */
export const updateInventory = async (data: any): Promise<any> => {
    try {
        validateArrayInput(data);
        const response = await materialAPI.put("/records", data);
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_UPDATE);
    }
};

/**
 * Fetch all inventory records
 */
export const fetchInventoryRecords = async (): Promise<any> => {
    try {
        const response = await materialAPI.get("/records");
        return response.data.records;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_FETCH);
    }
};

/**
 * Fetch inventory history with filters
 */
export const fetchInventoryHistory = async (filters: any): Promise<any> => {
    try {
        const response = await materialAPI.get("/history", { params: filters });
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_FETCH_HISTORY);
    }
};

/* =======================
 Material Types API Calls
 ======================= */

/**
 * Fetch all material types
 */
export const fetchMaterialTypes = async (options?: { force?: boolean }): Promise<any> => {
    try {
        const shouldBypassCache = options?.force === true;

        if (!shouldBypassCache && isCacheValid(materialTypesCache)) {
            return cloneArray(materialTypesCache!.data);
        }

        const response = await materialAPI.get("/types");
        const results = response.data.results;

        primeMaterialTypesCache(results);

        return cloneArray(results);
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_LOAD_TYPES);
    }
};

/**
 * Create new material types
 */
export const createMaterialTypes = async (materialTypes: string[]): Promise<any> => {
    try {
        validateArrayInput(materialTypes);
        const response = await materialAPI.post("/types", mapMaterialTypes(materialTypes));

        invalidateMaterialTypesCache();

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_CREATE_SIZES);
    }
};

/**
 * Update an existing material type
 */
export const updateMaterialType = async (id: number, newType: string): Promise<any> => {
    try {
        if (!id || !newType) throw new Error(Messages.ERROR_ALL_FIELDS_REQUIRED);
        const response = await materialAPI.put(`/types/${id}`, { material_type: newType });

        invalidateMaterialTypesCache();

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_UPDATE_MATERIAL + " type");
    }
};

/* =======================
 Material Sizes API Calls
 ======================= */

/**
 * Fetch all material sizes
 */
export const fetchMaterialSizes = async (options?: { force?: boolean }): Promise<any> => {
    try {
        const shouldBypassCache = options?.force === true;

        if (!shouldBypassCache && isCacheValid(materialSizesCache)) {
            return cloneArray(materialSizesCache!.data);
        }

        const response = await materialAPI.get("/sizes");
        const results = response.data.results;

        primeMaterialSizesCache(results);

        return cloneArray(results);
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_LOAD_SIZES);
    }
};

/**
 * Create new material sizes
 */
export const createMaterialSizes = async (sizes: string[]): Promise<any> => {
    try {
        validateArrayInput(sizes);
        const response = await materialAPI.post("/sizes", mapMaterialSize(sizes));

        invalidateMaterialSizesCache();

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_CREATE_SIZES);
    }
};

/**
 * Update an existing material size
 */
export const updateMaterialSize = async (id: number, newSize: string): Promise<any> => {
    try {
        if (!id || !newSize) throw new Error(Messages.ERROR_ALL_FIELDS_REQUIRED);
        const response = await materialAPI.put(`/sizes/${id}`, { size: newSize });

        invalidateMaterialSizesCache();

        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_UPDATE_MATERIAL + " size");
    }
};

export const invalidateMaterialCaches = () => {
    invalidateMaterialTypesCache();
    invalidateMaterialSizesCache();
};

/**
 * Delete material record by id
 */
export const deleteMaterialRecord = async (id: number): Promise<any> => {
    try {
        const response = await materialAPI.delete(`/records/${id}`);
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_DELETE_RECORD);
    }
};

/**
 * Update material record by id
 */
export const updateMaterialRecord = async (id: number, data: any): Promise<any> => {
    try {
        const response = await materialAPI.put(`/records/${id}`, data);
        return response.data;
    } catch (error: any) {
        throw new Error(error.response?.data?.message || Messages.ERROR_UPDATE_RECORD);
    }
};


export default materialAPI;
